import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-stretch">
      {/* Left brand / image panel */}
      <div className="hidden md:flex md:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Workshop"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1E3A8A]/85" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="text-3xl font-black tracking-tight">JAM</div>
            <div className="text-sm font-medium tracking-[0.2em] uppercase opacity-80 mt-1">
              Al Jamhour Al Mumayz
            </div>
          </div>
          <div>
            <h1 className="text-4xl font-black leading-tight">
              Field Sales<br />Command
            </h1>
            <p className="mt-4 text-white/80 max-w-sm font-medium">
              Know which garage is due for a visit, and never let a good customer go quiet.
            </p>
          </div>
          <div className="text-xs text-white/50 tracking-wide">Sharjah, UAE · Since 1996</div>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-[#F9FAFB] p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex flex-col items-center text-center mb-8">
            <img src="/jam-shield.png" alt="JAM shield" className="h-20 w-20 object-contain" />
            <div className="mt-3 text-lg font-black text-[#1E3A8A] leading-tight uppercase tracking-tight">Al Jamhour Al Mumayz<br />Auto Spare Parts</div>
            <div className="text-sm text-[#1E3A8A]/80 mt-1" dir="rtl">الجمهور المميز لتجارة قطع غيار السيارات</div>
            <div className="text-xs font-bold text-[#6B7280] mt-2 tracking-[0.15em]">SINCE 1996 · منذ عام ١٩٩٦</div>
          </div>
          <h2 className="text-2xl font-bold text-[#111827]">Sign in</h2>
          <p className="text-sm text-[#6B7280] mt-1 mb-6">Enter your account details</p>

          {error && (
            <div
              className="mb-4 text-sm font-medium text-[#B91C1C] bg-[#FEE2E2] border border-[#FECACA] rounded-md px-3 py-2"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <label className="block text-xs font-bold uppercase tracking-wide text-[#6B7280] mb-1">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email"
            className="w-full h-12 px-3 rounded-md border border-[#D1D5DB] bg-white text-[#111827] focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] mb-4"
            placeholder="you@jam.ae"
          />

          <label className="block text-xs font-bold uppercase tracking-wide text-[#6B7280] mb-1">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="login-password"
            className="w-full h-12 px-3 rounded-md border border-[#D1D5DB] bg-white text-[#111827] focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] mb-6"
            placeholder="••••••••"
          />

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full h-12 rounded-md bg-[#1E3A8A] text-white font-bold tracking-wide transition-colors duration-150 hover:bg-[#375DBA] active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
